import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2 } from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * After Stripe Checkout, we land here. The Stripe webhook updates the backend
 * asynchronously, so we poll /api/me/billing until the subscription is active
 * (or manual override is on), then refresh the cached `firm` in localStorage so
 * the plan gate in App.js will release the user to the dashboard.
 */
export default function PaymentSuccess() {
  const [status, setStatus] = useState('activating'); // activating | ready | timeout | unauthenticated
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  useEffect(() => {
    if (!token) { setStatus('unauthenticated'); return; }

    let cancelled = false;
    const maxAttempts = 15; // ~30s total
    let attempt = 0;

    const tick = async () => {
      attempt += 1;
      try {
        const res = await fetch(`${API}/me/billing`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('billing-fetch-failed');
        const b = await res.json();
        const active = b?.subscription_active || b?.billing_status === 'active' || b?.billing_status === 'trialing' || b?.manual_override_enabled;
        if (active) {
          // Merge latest billing into cached firm so the App.js plan gate passes
          try {
            const storedFirm = JSON.parse(localStorage.getItem('firm') || '{}');
            const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
            const nextFirm = {
              ...storedFirm,
              plan_config: b.plan_config || storedFirm.plan_config,
              subscription_plan: b.subscription_plan || storedFirm.subscription_plan,
              subscription_active: b.subscription_active,
              billing_status: b.billing_status,
              billing_interval: b.billing_interval,
              billing_currency: b.billing_currency,
              current_period_end: b.current_period_end,
              manual_override_enabled: b.manual_override_enabled
            };
            localStorage.setItem('firm', JSON.stringify(nextFirm));
            // Keep user.firm.plan_config mirror if present
            if (storedUser?.firm) {
              storedUser.firm = { ...storedUser.firm, ...nextFirm };
              localStorage.setItem('user', JSON.stringify(storedUser));
            }
          } catch (_e) { /* ignore cache errors */ }
          if (!cancelled) setStatus('ready');
          return;
        }
      } catch (_e) { /* retry */ }
      if (attempt >= maxAttempts) {
        if (!cancelled) setStatus('timeout');
        return;
      }
      setTimeout(tick, 2000);
    };
    tick();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg text-center">
        <div className="flex flex-col items-center mb-8">
          <BrandLogo size={56} />
          <h1 className="mt-4 text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Law Agency 360</h1>
        </div>
        <div className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl p-10 shadow-2xl" data-testid="payment-success-card">
          <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center mb-5">
            <CheckCircle2 className="w-8 h-8 text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit' }}>Payment successful</h2>

          {status === 'activating' && (
            <div className="flex items-center justify-center gap-2 text-slate-300 mb-6" data-testid="payment-success-activating">
              <Loader2 className="w-4 h-4 animate-spin text-violet-300" />
              <span>Activating your dashboard…</span>
            </div>
          )}

          {status === 'ready' && (
            <>
              <p className="text-lg font-semibold text-white mb-2" data-testid="payment-success-thankyou">Thanks for subscribing! 🎉</p>
              <p className="text-slate-300 mb-6" data-testid="payment-success-ready">
                Now you can sit back and relax while we take care of the work. Your dedicated account manager will be in touch shortly to get everything moving.
              </p>
              <button
                onClick={() => window.location.assign('/dashboard/overview')}
                className="inline-block w-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white py-3 rounded-xl font-semibold transition-all duration-200 hover:shadow-xl hover:shadow-violet-600/40"
                data-testid="payment-success-go-to-dashboard"
              >
                Go to Dashboard
              </button>
            </>
          )}

          {status === 'timeout' && (
            <>
              <p className="text-slate-300 mb-2">We received your payment. Activation is still processing.</p>
              <p className="text-xs text-slate-500 mb-6">Please click below to retry - your access will unlock once the payment is confirmed.</p>
              <button
                onClick={() => window.location.assign('/dashboard/overview')}
                className="inline-block w-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white py-3 rounded-xl font-semibold transition-all duration-200 hover:shadow-xl hover:shadow-violet-600/40"
                data-testid="payment-success-retry"
              >
                Continue to Dashboard
              </button>
            </>
          )}

          {status === 'unauthenticated' && (
            <>
              <p className="text-slate-300 mb-6">Please sign in to activate your new plan.</p>
              <Link
                to="/auth?mode=signin"
                className="inline-block w-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white py-3 rounded-xl font-semibold transition-all duration-200 hover:shadow-xl hover:shadow-violet-600/40"
                data-testid="payment-success-signin"
              >
                Sign in
              </Link>
            </>
          )}

          <Link
            to="/auth?mode=signin"
            className="block text-center text-sm text-slate-400 hover:text-violet-300 mt-4"
          >
            Sign in with a different account
          </Link>
        </div>
      </div>
    </div>
  );
}
